import React, { Button, Icon } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Separator from 'common/components/Separator';
import featureFlags from 'common/helpers/featureFlags';
import { useSelector } from 'react-redux';

import { USER_ROLES, NavigationNodeId } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import {
  addAnalysisTypePath,
  addContentSetTypePath,
  createHypothesisPath,
  createProjectTopicPath,
} from 'common/services/urlService';
import { number } from 'prop-types';
import { useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './AddProjectItemsHeaderMenu.scss';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import useMobile from '../../../hooks/useMobile';

const iconSize = 24;

const AddProjectItemsHeaderMenu = ({ projectId, isProjectDisabled }) => {
  const addHypothesisHref = createHypothesisPath(projectId);
  const addAnalysisTypeHref = addAnalysisTypePath(projectId);
  const addContentSetTypeHref = addContentSetTypePath(projectId);
  const addProjectTopicHref = createProjectTopicPath(projectId);

  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const ref = useRef(null);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isMobile = useMobile();
  const { t } = useTranslation();

  const handleClick = (event) => {
    setShow(!show);
    setTarget(event.target);
  };

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      document.body.click();
    }, 200);
  };
  const renderAddAnalysisOption = () => {
    const path = addAnalysisTypeHref;
    const text = 'addAnAnalysis';

    return (
      <Link
        data-testid="add-analysis-button"
        className={classnames('dropdown-item', {
          'dropdown-item-disabled': isMobile,
        })}
        to={path}
        onClick={handleClick}
      >
        <Icon
          name="analysis"
          width={iconSize}
          className="outline icon-orange-theme"
        />
        {t(text)}
      </Link>
    );
  };

  const renderAddContentSet = () => {
    const path = addContentSetTypeHref;
    const text = 'addAContentSet';

    return (
      <>
        <Separator />
        <Link
          data-testid="add-content-set-button"
          className="dropdown-item"
          to={path}
          onClick={handleClick}
        >
          <Icon
            name="grid"
            width={iconSize}
            className="outline icon-orange-theme"
          />
          {t(text)}
        </Link>
      </>
    );
  };

  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);

  return (
    <>
      <div className="add-project-items-header-menu" ref={ref}>
        <Button
          data-testid="header-fab"
          id="header-fab"
          className="button-avoid-focus add-icon button-link add-popover-button"
          onClick={handleClick}
          title="Add Content"
          disabled={isProjectDisabled}
        >
          <Icon
            name="add"
            className={classnames('fill', {
              'icon-orange': !isProjectDisabled,
              'icon-disabled': isProjectDisabled,
            })}
            width={isMobile ? 24 : 32}
          />
        </Button>
        <Overlay
          show={show}
          target={target}
          placement="bottom"
          rootClose
          onHide={() => {
            setShow(!show);
          }}
          container={ref.current}
          popperConfig={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 12],
                },
              },
            ],
          }}
        >
          <Popover
            id="popover-add"
            ref={ref}
            className="header-add-popover"
            data-testid="header-add-popover"
          >
            <Popover.Content className="popup-user">
              {featureFlags.isPOTopicRefactorEnabled && (
                <Link
                  data-testid="add-po-topic-button"
                  className={classnames('dropdown-item', {
                    'dropdown-item-disabled': isMobile,
                  })}
                  to={addProjectTopicHref}
                  onClick={handleClick}
                >
                  <Icon
                    name="folder-closed"
                    width={iconSize}
                    className="outline icon-orange-theme"
                  />
                  {t('addATopic')}
                </Link>
              )}
              <Link
                data-testid="add-hypothesis-button"
                className={classnames('dropdown-item', {
                  'dropdown-item-disabled': isMobile,
                })}
                to={addHypothesisHref}
                onClick={handleClick}
              >
                <Icon
                  name="hypothesis"
                  width={iconSize}
                  className="outline icon-orange-theme"
                />
                {t('addKeyFocusArea', { KFATitle })}
              </Link>
              {renderAddAnalysisOption()}
              {featureFlags.isProjectImportEnabled &&
                isPwcUser &&
                renderAddContentSet()}
            </Popover.Content>
          </Popover>
        </Overlay>
      </div>
      {show && (
        <div
          className="add-header-popover-overlay"
          onClick={handleClose}
          data-testid="add-header-popover-overlay"
          role="presentation"
        />
      )}
    </>
  );
};

AddProjectItemsHeaderMenu.propTypes = {
  projectId: number.isRequired,
};

export default AddProjectItemsHeaderMenu;
